import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getUserOutfits, deleteUserOutfits } from '@/services/usersService';
import { Outfit } from '@/services/outfitsService';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Trash2, ImageIcon, Loader2 } from 'lucide-react';

const UserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingAll, setDeletingAll] = useState(false);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        setLoading(true);
        const data = await getUserOutfits(id);
        setOutfits(data as Outfit[]);
      } catch (error: any) {
        console.error('Error fetching user outfits:', error);
        toast.error('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleDeleteAllOutfits = async () => {
    if (!id) return;
    try {
      setDeletingAll(true);
      const count = await deleteUserOutfits(id);
      setOutfits([]);
      toast.success(`Deleted ${count} outfit(s)`);
    } catch (error: any) {
      console.error('Error deleting outfits:', error);
      toast.error('Failed to delete outfits');
    } finally {
      setDeletingAll(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/users">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">User Details</h1>
          <p className="text-muted-foreground font-mono text-sm">{id}</p>
        </div>
        {outfits.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete All Outfits
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-background border border-border/50">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete All Outfits</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all {outfits.length} outfit(s) by this user. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-background hover:bg-muted">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-white hover:bg-destructive/90"
                  onClick={handleDeleteAllOutfits}
                  disabled={deletingAll}
                >
                  {deletingAll ? 'Deleting...' : 'Delete All'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>User Outfits ({outfits.length})</CardTitle>
          <CardDescription>All outfits created by this user</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                <span className="text-sm text-muted-foreground">Loading user data...</span>
              </div>
            </div>
          ) : outfits.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium">No outfits</h3>
                <p className="text-muted-foreground">This user hasn't created any outfits</p>
              </div>
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Image</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Views</TableHead>
                    <TableHead>Likes</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outfits.map((outfit) => (
                    <TableRow key={outfit.id}>
                      <TableCell>
                        <div className="w-10 h-10 rounded-md bg-muted overflow-hidden">
                          {outfit.image_url ? (
                            <img
                              src={outfit.image_url}
                              alt={outfit.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/placeholder.svg';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ImageIcon className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Link to={`/admin/outfits/${outfit.id}`} className="font-medium hover:text-primary transition-colors">
                          {outfit.title}
                        </Link>
                      </TableCell>
                      <TableCell>{outfit.view_count}</TableCell>
                      <TableCell>{outfit.like_count}</TableCell>
                      <TableCell>
                        <Badge variant={outfit.is_public ? 'default' : 'secondary'}>
                          {outfit.is_public ? 'Public' : 'Private'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {new Date(outfit.created_at).toLocaleDateString()}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/admin/outfits/${outfit.id}`}>View</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserDetail;
